"use client";

import { cn, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import Image from '@/components/ui/Image';

interface ContentBlockProps {
  backgroundColor?: string;
  badge?: string;
  content?: string;
  title?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  image?: string;
  imageAltText?: string;
  className?: string;
  blockBackground?: string;
}

interface Feature14Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  themeChangeOnScroll?: boolean;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  leftContent?: ContentBlockProps;
  rightContent?: ContentBlockProps;
  attributes?: React.HTMLProps<HTMLElement>;
}

const ContentBlock = ({
  backgroundColor,
  badge,
  content,
  title,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  image,
  imageAltText,
  className,
  blockBackground
}: ContentBlockProps) => (
  <div 
    className={cn(
      className, 
      "rounded-xl pt-6 px-6 md:pt-7 md:px-14 shadow-xl", 
      !backgroundColor && blockBackground
    )}
    style={backgroundColor ? { backgroundColor: backgroundColor } : {}}
  >
    <div className="flex flex-col justify-between items-center h-full">
      <div className="flex flex-col gap-4  p-6 pb-0">
        {badge && (
          <Badge variant="outline" bold className="m-auto block">
            {badge}
          </Badge>
        )}
        
        {title && (
          <h4 className="text-center mt-2 w-[85%] mx-auto text-primary">
            {parse(title)}
          </h4>
        )}
        {content && (
          <div className="paragraph-base text-muted-foreground text-center">
            {parse(content)}
          </div>
        )}
      </div>
      {(hasButton || image) && (
        <div className="w-full flex flex-col items-center">
          {hasButton && (
            <div className="flex flex-col">
              <Button asChild variant={buttonStyle} size="lg" className="w-full mt-6 md:mt-10">
                <Link href={buttonLink || ""}>
                  {buttonText}
                </Link>
              </Button>
              {microCopy && (
                <p className="paragraph-sm  text-muted-foreground text-center mt-3">
                  {microCopy}
                </p>
              )}
            </div>
          )}
          {image && (
            <div className="relative  w-full mt-10">
              <Image src={image} alt={imageAltText || ""} title={imageAltText || ""} width={540} height={540} className="object-cover aspect-[540/540] rounded-t-xl w-full" />
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);


const Feature14 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  usedInPageNav = false,
  pageNavTitle = "Feature 14",
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  leftContent,
  rightContent,
  attributes,
}: Feature14Props) => {
  const blockBackground = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature14"
      attributes={attributes}
    >
      <div className="container"> 
        {(badge || title || content || hasButton) && (
          <div className="flex flex-col gap-6 text-center justify-center mb-10 lg:mb-20" data-aos="fade-up">
            {badge && (
              <Badge variant="outline" bold className="m-auto">
                {badge}
              </Badge>
            )}
            {title && (
              <h2 className="max-w-[500px] lg:max-w-[800px] mx-auto">
                {parse(title)}
              </h2>
            )}
            {content && (
              <div className="paragraph-lg text-muted-foreground max-w-[58ch] mx-auto">
                {parse(content)}
              </div>
            )}
            {hasButton && (
              <div className="flex flex-col items-center mt-4">
                <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                  <Link href={buttonLink ?? ""}>
                    {buttonText}
                  </Link>
                </Button>
                {microCopy && (
                  <p className="paragraph-sm text-muted-foreground text-center mt-3">
                    {microCopy}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-2 light" data-aos="fade-up" data-aos-delay="100">
          <ContentBlock {...leftContent} blockBackground={cn(blockBackground)} />
          <ContentBlock {...rightContent} blockBackground={cn(blockBackground)} />
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Feature14 };